import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Grid, Chip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneIcon from '@mui/icons-material/Phone';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useLang } from '../context/LanguageContext';

const bgImages = [
  'linear-gradient(135deg, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.52) 100%), url("https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&q=80") center/cover no-repeat',
  'linear-gradient(135deg, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.52) 100%), url("https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1600&q=80") center/cover no-repeat',
  'linear-gradient(135deg, rgba(0,0,0,0.84) 0%, rgba(150,10,20,0.42) 100%), url("https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&q=80") center/cover no-repeat',
];

export default function Hero() {
  const { t } = useLang();
  const h = t.hero;
  const [current, setCurrent] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % h.slides.length);
        setFade(true);
      }, 500);
    }, 5500);
    return () => clearInterval(interval);
  }, [h.slides.length]);

  const slide = h.slides[current];

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        minHeight: { xs: '85vh', md: '90vh' },
        background: bgImages[current],
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.8s ease',
      }}
    >
      {/* White diagonal bottom cutout */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: { xs: '60px', md: '100px' },
        background: '#fff',
        clipPath: 'polygon(0 100%, 100% 100%, 100% 50%, 0 100%)',
        zIndex: 1,
      }} />

      {/* Left red accent bar */}
      <Box sx={{
        position: 'absolute',
        left: 0,
        top: '15%',
        bottom: '20%',
        width: 5,
        background: '#C8102E',
        zIndex: 2,
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, px: { xs: 3, sm: 4 }, py: { xs: 6, md: 6 } }}>
        <Grid container>
          <Grid item xs={12} md={9} lg={7}>
            {/* Category tag */}
            <Box sx={{ opacity: fade ? 1 : 0, transition: 'opacity 0.5s ease', mb: 2 }}>
              <Chip
                label={slide.tag}
                size="small"
                sx={{
                  background: '#C8102E',
                  color: '#fff',
                  fontFamily: '"Oswald", sans-serif',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontSize: '0.68rem',
                  borderRadius: 0,
                  height: 26,
                }}
              />
            </Box>

            {/* Headline */}
            <Typography
              variant="h1"
              sx={{
                color: '#FFFFFF',
                fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.5rem', lg: '5.4rem' },
                lineHeight: 1.0,
                mb: { xs: 2.5, md: 3 },
                opacity: fade ? 1 : 0,
                transform: fade ? 'translateY(0)' : 'translateY(12px)',
                transition: 'all 0.6s ease',
                textShadow: '0 2px 20px rgba(0,0,0,0.4)',
                whiteSpace: 'pre-line',
              }}
            >
              {slide.headline}
            </Typography>

            {/* Subtext */}
            <Typography
              sx={{
                color: '#DDDDDD',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                maxWidth: 520,
                mb: { xs: 3.5, md: 4 },
                lineHeight: 1.75,
                fontWeight: 300,
                opacity: fade ? 1 : 0,
                transition: 'opacity 0.7s ease 0.1s',
              }}
            >
              {slide.subtext}
            </Typography>

            {/* CTA Buttons */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={{ xs: 4, md: 5 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => scrollTo('#contact')}
                sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, px: { xs: 3, md: 4 }, py: 1.4 }}
              >
                {h.ctaPrimary}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<PhoneIcon />}
                href={`tel:${h.ctaPhone.replace(/\D/g, '')}`}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.45)',
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  px: { xs: 3, md: 4 },
                  py: 1.4,
                  '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.08)' },
                }}
              >
                {h.ctaPhone}
              </Button>
            </Box>

            {/* Trust badges */}
            <Box display="flex" flexWrap="wrap" gap={{ xs: 1.5, md: 2 }}>
              {h.badges.map((badge) => (
                <Box key={badge} display="flex" alignItems="center" gap={0.5}>
                  <VerifiedIcon sx={{ color: '#C8102E', fontSize: 15 }} />
                  <Typography sx={{
                    color: '#CCCCCC',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}>
                    {badge}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Slide indicators */}
        <Box display="flex" gap={1} mt={5}>
          {h.slides.map((_, i) => (
            <Box
              key={i}
              onClick={() => setCurrent(i)}
              sx={{
                width: i === current ? 30 : 8,
                height: 4,
                background: i === current ? '#C8102E' : 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                transition: 'all 0.4s ease',
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
}
